import React from "react";
import { Link } from "react-router-dom";
import MainFooter from "../shared/MainFooter.jsx";
import { LEGAL_COMPANY } from "@/lib/legalCompany";

const LegalPageShell = ({ title, children }) => (
  <div className="choice-landing min-h-screen bg-[#080b14] noise-overlay">
    <header className="border-b border-white/[0.05]">
      <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center">
          <img src="/images/logo.png" alt={LEGAL_COMPANY.brandName} className="w-full max-w-[100px]" />
        </Link>
        <Link to="/" className="text-sm text-gray-400 hover:text-white transition-colors">
          ← Back to home
        </Link>
      </div>
    </header>

    <main className="max-w-3xl mx-auto px-6 py-16">
      <h1 className="text-3xl md:text-4xl font-semibold text-white mb-8">{title}</h1>
      <div className="prose prose-invert prose-sm max-w-none text-gray-400 space-y-6">{children}</div>
    </main>

    <MainFooter />
  </div>
);

const linkClass = "text-teal-400 hover:text-teal-300";

const PrivacyPolicyPage = () => (
  <LegalPageShell title="Privacy Policy">
    <p className="text-gray-500 text-sm">Last updated: June 2025</p>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">1. Introduction</h2>
      <p>
        Welcome to {LEGAL_COMPANY.brandName}. We respect your privacy and are committed to protecting your personal data. This Privacy
        Policy explains how we collect, use, disclose, and safeguard your information when you visit our website, submit
        inquiries (including Custom Agent forms), and use our AI employee and automation services.{" "}
        <strong className="text-white">
          This Privacy Policy should be read in conjunction with our{" "}
          <Link to="/terms" className={linkClass}>
            Terms of Service
          </Link>
          .
        </strong>
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">2. Information We Collect</h2>
      <p>
        We collect information that identifies, relates to, or could reasonably be linked to you. Under applicable US
        state laws (e.g., CCPA/CPRA), the personal information we collect falls into the following categories:
        Identifiers (e.g., name, email), Commercial Information (e.g., business details), and Internet or other
        electronic network activity (e.g., IP address, browser type).
      </p>
      <p>This includes:</p>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong className="text-white">Information You Provide to Us:</strong> When you interact with our website,
          request a demo, create an account, or contact support, we may collect your name, email address, phone number,
          company name, business details, and any other information you choose to provide in your inquiries.
        </li>
        <li>
          <strong className="text-white">Information Collected Automatically:</strong> When you navigate our marketing
          sites and platforms, we automatically collect usage details, IP addresses, browser types, operating systems,
          referring URLs, and information collected through cookies and tracking technologies to analyze traffic and
          improve user experience.
        </li>
        <li>
          <strong className="text-white">Client Lead Data (Data Processor Role):</strong> When you deploy {LEGAL_COMPANY.brandName} AI
          agents on your website or communications channels, we act as a <strong className="text-white">Data Processor</strong>{" "}
          (or Service Provider). We process information from your end-users (leads) strictly as instructed by you to
          provide our automated response and lead qualification services. It is your responsibility as the{" "}
          <strong className="text-white">Data Controller</strong> to ensure your own website and business have
          appropriate privacy policies and consents regarding lead capture.
        </li>
      </ul>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">3. How We Use Your Information</h2>
      <p>We use the collected information for the following purposes:</p>
      <ul className="list-disc pl-5 space-y-2">
        <li>To deliver, operate, and maintain our AI employee and automation services.</li>
        <li>To respond to your inquiries, support requests, and Custom Agent submissions.</li>
        <li>To communicate with you about your account, deployment updates, billing, and system notifications.</li>
        <li>To send marketing and promotional communications (you can opt out at any time).</li>
        <li>To analyze usage trends, monitor system performance, and improve our platform.</li>
        <li>To comply with legal obligations, enforce our terms of service, and protect our legal rights.</li>
      </ul>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">4. Data Sharing and Disclosure</h2>
      <p>
        <strong className="text-white">
          We do not sell your personal information, nor do we share it with third parties for cross-context behavioral
          advertising.
        </strong>{" "}
        We may disclose your information only under the following limited circumstances:
      </p>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong className="text-white">Service Providers:</strong> We share data with trusted third-party vendors who
          assist us in operating our platform, subject to strict confidentiality obligations. This includes hosting
          providers, infrastructure services, analytics providers, payment processors (e.g., Stripe), and enterprise AI
          model providers.
        </li>
        <li>
          <strong className="text-white">Legal Requirements &amp; Protection:</strong> We may disclose your information
          if required to do so by law, court order, or valid government request, or to protect the safety, rights, or
          property of {LEGAL_COMPANY.brandName}, our users, or the public.
        </li>
        <li>
          <strong className="text-white">Business Transfers:</strong> In the event of a merger, acquisition,
          reorganization, or sale of assets, user data may be transferred as part of that business transaction.
        </li>
      </ul>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">5. Data Security &amp; AI Processing</h2>
      <p>
        We implement appropriate technical and organizational measures (including SSL/TLS encryption, restricted access
        controls, and secure server environments) to protect your personal information against unauthorized access,
        loss, or alteration. In the event of a personal data breach that compromises your information, we will notify
        you and the relevant supervisory authorities as required by applicable law.
      </p>
      <p>
        <strong className="text-white">AI Data Handling Guarantee:</strong>
        <br />
        Data processed by our AI models is transmitted securely. We utilize enterprise-grade APIs and{" "}
        <strong className="text-white">do not allow</strong> third-party AI providers (such as OpenAI, Anthropic, or
        others) to use your business data, chat logs, customer interactions, or lead information to train their public
        or proprietary AI models.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">6. International Data Transfers</h2>
      <p>
        {LEGAL_COMPANY.brandName} operates globally. Your personal information and data may be transferred to, stored, and processed in
        countries outside of your state, province, or country of residence (including the United States), where data
        protection and privacy laws may differ from those in your jurisdiction.
      </p>
      <p>
        By using our website and services, you consent to the transfer of your information to these facilities. We take
        all reasonable steps to ensure that your data is treated securely and receives an adequate level of protection
        in accordance with applicable laws (including Australian Privacy Principle 8 and New Zealand Information Privacy
        Principle 12).
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">7. Data Retention</h2>
      <p>
        We retain your personal information and client lead data only for as long as necessary to fulfill the purposes
        outlined in this Privacy Policy, deliver our services to you, resolve disputes, maintain audit logs, and enforce
        our legal agreements. When data is no longer needed, it is securely erased or permanently anonymized.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">8. Cookies &amp; Tracking Signals</h2>
      <p>
        Our websites use essential cookies to enable core functionality and performance analytics cookies to help us
        understand how visitors interact with our pages. You can control or block cookies through your browser settings,
        though doing so may impact certain site features.
      </p>
      <p>
        <strong className="text-white">Do Not Track &amp; Global Privacy Control:</strong>
        <br />
        Where required by law, our website recognizes automated privacy preference signals, such as Global Privacy
        Control (GPC) signals or &quot;Do Not Track&quot; browser headers.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">9. Your Privacy Rights</h2>
      <p>
        Depending on your location (including under the EU/UK GDPR, California CCPA/CPRA and other applicable US State
        Privacy Laws, Australian Privacy Act 1988, and New Zealand Privacy Act 2020), you may have the following rights
        regarding your personal information:
      </p>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong className="text-white">Access &amp; Portability:</strong> The right to request copies of the personal
          data we hold about you.
        </li>
        <li>
          <strong className="text-white">Correction:</strong> The right to request correction of inaccurate, incomplete,
          or outdated personal information.
        </li>
        <li>
          <strong className="text-white">Deletion:</strong> The right to request the deletion of your personal data
          (&quot;Right to be Forgotten&quot;), subject to legal exceptions.
        </li>
        <li>
          <strong className="text-white">Opt-Out of Marketing:</strong> You can opt out of receiving promotional
          communications at any time by clicking the &quot;unsubscribe&quot; link in any of our emails or contacting us
          directly.
        </li>
        <li>
          <strong className="text-white">Anonymity &amp; Pseudonymity:</strong> Where lawful and practicable (such as
          when submitting general informational inquiries), you have the right to interact with us anonymously or by
          using a pseudonym.
        </li>
        <li>
          <strong className="text-white">Non-Discrimination:</strong> We will not discriminate against you (e.g., deny
          services or charge different prices) for exercising any of your privacy rights.
        </li>
      </ul>
      <p>
        To exercise any of these rights, please contact us at{" "}
        <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
          {LEGAL_COMPANY.supportEmail}
        </a>
        . We will respond to your privacy requests within the timeframes required by applicable law (typically within 30
        to 45 days).
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">10. Children&apos;s Privacy</h2>
      <p>
        Our services are designed exclusively for businesses and working professionals. They are not intended for
        individuals under the age of 18. We do not knowingly collect personal information from children. If we become
        aware that we have inadvertently collected data from a minor, we will take immediate steps to delete it.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">11. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time to reflect changes in our legal obligations, technology, or
        business practices. We will notify you of any material changes by posting the updated document on this page and
        updating the &quot;Last updated&quot; date at the top. Your continued use of our services after any updates
        constitutes acceptance of the revised policy.
      </p>
    </section>

    <section className="space-y-4">
      <h2 className="text-xl text-white font-medium">12. Contact Us &amp; Complaints</h2>
      <p>
        If you have any questions, concerns, or requests regarding this Privacy Policy or how we handle your personal
        data, please contact us:
      </p>
      <div className="space-y-2 not-prose">
        <p>
          <span className="text-gray-500">Company:</span> {LEGAL_COMPANY.legalName}
        </p>
        <p>
          <span className="text-gray-500">ACN:</span> {LEGAL_COMPANY.acn}
        </p>
        <p>
          <span className="text-gray-500">ABN:</span> {LEGAL_COMPANY.abn}
        </p>
        <p>
          <span className="text-gray-500">Email:</span>{" "}
          <a href={`mailto:${LEGAL_COMPANY.supportEmail}`} className={linkClass}>
            {LEGAL_COMPANY.supportEmail}
          </a>
        </p>
        <p>
          <span className="text-gray-500">Address:</span> {LEGAL_COMPANY.address}
        </p>
      </div>
      <p>
        <strong className="text-white">Complaints Mechanism (Australia &amp; New Zealand):</strong> If you have a privacy
        complaint, please contact us first. We will review your request and respond within a reasonable timeframe
        (typically within 30 days). If you are not satisfied with our response, you have the right to lodge a complaint
        with your local regulatory authority:
      </p>
      <ul className="list-disc pl-5 space-y-2">
        <li>
          <strong className="text-white">Australia:</strong> Office of the Australian Information Commissioner (OAIC) —{" "}
          <a href="https://www.oaic.gov.au" target="_blank" rel="noopener noreferrer" className={linkClass}>
            www.oaic.gov.au
          </a>
        </li>
        <li>
          <strong className="text-white">New Zealand:</strong> Office of the Privacy Commissioner (OPC) —{" "}
          <a href="https://www.privacy.org.nz" target="_blank" rel="noopener noreferrer" className={linkClass}>
            www.privacy.org.nz
          </a>
        </li>
      </ul>
    </section>
  </LegalPageShell>
);

export default PrivacyPolicyPage;
